import {
  MAX_TEAM_EVENT_REPEATS,
  TEAM_EVENT_KIND_LABELS,
  TEAM_EVENT_KINDS,
  teamCalendarSchema,
  teamEventKindSchema,
  type AdminTeam,
  type TeamCalendar,
  type TeamEvent,
  type TeamEventKind,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  DateTimeInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  Textarea,
} from '@growup/ui';
import { useEffect, useState, type FormEvent } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatEventDay, isoToLisbonInput, lisbonInputToIso } from '../events/labels';

const BASE = '/api/site/teams';

const lisbon = { timeZone: 'Europe/Lisbon' } as const;
const dayKey = new Intl.DateTimeFormat('en-CA', lisbon);
const timeFormat = new Intl.DateTimeFormat('pt-PT', {
  ...lisbon,
  hour: '2-digit',
  minute: '2-digit',
});

/** `ter., 23/03 · 18:00–20:00` (hora de Lisboa). */
export function formatTeamEventWhen(startsAt: string, endsAt: string | null): string {
  const start = new Date(startsAt);
  const time = endsAt
    ? `${timeFormat.format(start)}–${timeFormat.format(new Date(endsAt))}`
    : timeFormat.format(start);
  return `${formatEventDay(dayKey.format(start))} · ${time}`;
}

/** `Online`, o local, ou os dois. */
export function teamEventPlace(e: Pick<TeamEvent, 'online' | 'location'>): string {
  if (e.online) return e.location ? `Online · ${e.location}` : 'Online';
  return e.location || 'Local por definir';
}

/**
 * Calendario de treinos e jogos de uma equipa (parte 5, docs/equipas). Os
 * jogos aparecem na pagina publica (sem notas); os treinos so a quem esta na
 * equipa e aos encarregados.
 */
export function CalendarCard({ team }: { team: AdminTeam }) {
  const [calendar, setCalendar] = useState<TeamCalendar | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const archived = team.status === 'archived';

  useEffect(() => {
    getJson(`${BASE}/${team.id}/calendar`, teamCalendarSchema)
      .then(setCalendar)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [team.id]);

  const run = async (method: string, path: string, body?: unknown): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      setCalendar(teamCalendarSchema.parse(await send(method, `${BASE}${path}`, body)));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (calendar === null) {
    return (
      <Card headingLevel={2} title="Calendário">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }

  return (
    <Card headingLevel={2} title="Calendário">
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Os jogos aparecem na página pública da equipa; os treinos e as notas só a quem está na
          equipa e aos encarregados dos jovens.
        </p>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {calendar.upcoming.length === 0 ? <p>Sem treinos nem jogos marcados.</p> : null}
        <ul className="flex flex-col divide-y divide-gu-line">
          {calendar.upcoming.map((e) =>
            editing === e.id ? (
              <li key={e.id} className="py-3">
                <EventForm
                  initial={e}
                  busy={busy}
                  submitLabel="Guardar"
                  onCancel={() => setEditing(null)}
                  onSubmit={(body) =>
                    run('PUT', `/calendar/${e.id}`, body).then((ok) => {
                      if (ok) setEditing(null);
                      return ok;
                    })
                  }
                />
              </li>
            ) : (
              <li key={e.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
                <EventSummary event={e} />
                <div className="flex flex-wrap justify-end gap-2 sm:ml-auto">
                  <Button size="sm" variant="secondary" onClick={() => setEditing(e.id)}>
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      void run('PUT', `/calendar/${e.id}/cancelled`, { cancelled: !e.cancelled })
                    }
                  >
                    {e.cancelled ? 'Repor' : 'Cancelar'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm('Apagar este treino ou jogo?')) {
                        void run('DELETE', `/calendar/${e.id}`);
                      }
                    }}
                  >
                    Apagar
                  </Button>
                </div>
              </li>
            ),
          )}
        </ul>
        {archived ? null : adding ? (
          <div className="border-2 border-gu-line p-3">
            <EventForm
              busy={busy}
              submitLabel="Marcar"
              repeat
              onCancel={() => setAdding(false)}
              onSubmit={(body) =>
                run('POST', `/${team.id}/calendar`, body).then((ok) => {
                  if (ok) setAdding(false);
                  return ok;
                })
              }
            />
          </div>
        ) : (
          <div>
            <Button size="sm" onClick={() => setAdding(true)}>
              Marcar treino ou jogo
            </Button>
          </div>
        )}
        {calendar.past.length > 0 ? (
          <div className="flex flex-col gap-1">
            <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
              Anteriores
            </h3>
            <ul className="text-gu-ink-muted">
              {calendar.past.map((e) => (
                <li key={e.id}>
                  {formatTeamEventWhen(e.startsAt, e.endsAt)} · {TEAM_EVENT_KIND_LABELS[e.kind]}
                  {e.opponent ? ` contra ${e.opponent}` : ''}
                  {e.cancelled ? ' · cancelado' : ''}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function EventSummary({ event: e }: { event: TeamEvent }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={e.kind === 'match' ? 'info' : 'neutral'}>
          {TEAM_EVENT_KIND_LABELS[e.kind]}
        </StatusBadge>
        {e.cancelled ? <StatusBadge tone="danger">Cancelado</StatusBadge> : null}
        <span className={e.cancelled ? 'font-semibold line-through' : 'font-semibold'}>
          {formatTeamEventWhen(e.startsAt, e.endsAt)}
        </span>
      </div>
      {e.kind === 'match' && (e.opponent || e.competition) ? (
        <p>
          {e.opponent ? `Contra ${e.opponent}` : ''}
          {e.opponent && e.competition ? ' · ' : ''}
          {e.competition}
        </p>
      ) : null}
      <p className="text-caption text-gu-ink-muted">{teamEventPlace(e)}</p>
      {e.notes ? <p className="text-caption text-gu-ink-muted">Notas: {e.notes}</p> : null}
    </div>
  );
}

function EventForm({
  initial,
  busy,
  submitLabel,
  repeat = false,
  onCancel,
  onSubmit,
}: {
  initial?: TeamEvent;
  busy: boolean;
  submitLabel: string;
  /** Ao criar: repetir nas semanas seguintes. */
  repeat?: boolean;
  onCancel: () => void;
  onSubmit: (body: unknown) => Promise<boolean>;
}) {
  const [kind, setKind] = useState<TeamEventKind>(initial?.kind ?? 'training');
  const [startsAt, setStartsAt] = useState(isoToLisbonInput(initial?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(isoToLisbonInput(initial?.endsAt ?? null));
  const [online, setOnline] = useState(initial?.online ?? false);
  const [location, setLocation] = useState(initial?.location ?? '');
  const [opponent, setOpponent] = useState(initial?.opponent ?? '');
  const [competition, setCompetition] = useState(initial?.competition ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [weeks, setWeeks] = useState('0');
  const [fields, setFields] = useState<Record<string, string>>({});
  const match = kind === 'match';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problems: Record<string, string> = {};
    const starts = lisbonInputToIso(startsAt);
    const ends = endsAt === '' ? null : lisbonInputToIso(endsAt);
    if (starts === null) problems.startsAt = 'Indique a data e a hora.';
    if (endsAt !== '' && ends === null) problems.endsAt = 'Data e hora inválidas.';
    else if (starts && ends && ends <= starts)
      problems.endsAt = 'O fim tem de ser depois do início.';
    const repeatWeeks = Number(weeks || '0');
    if (repeat && (repeatWeeks < 0 || repeatWeeks > MAX_TEAM_EVENT_REPEATS)) {
      problems.weeks = `De 0 a ${String(MAX_TEAM_EVENT_REPEATS)}.`;
    }
    setFields(problems);
    if (Object.keys(problems).length > 0) return;
    void onSubmit({
      kind,
      startsAt: starts,
      endsAt: ends,
      online,
      location,
      opponent: match ? opponent : '',
      competition: match ? competition : '',
      notes,
      ...(repeat ? { repeatWeeks } : {}),
    });
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Tipo">
          <Select value={kind} onChange={(e) => setKind(teamEventKindSchema.parse(e.target.value))}>
            {TEAM_EVENT_KINDS.map((k) => (
              <option key={k} value={k}>
                {TEAM_EVENT_KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Início" hint="Hora de Lisboa." error={fields.startsAt}>
          <DateTimeInput value={startsAt} onChange={setStartsAt} />
        </Field>
        <Field label="Fim" optional error={fields.endsAt}>
          <DateTimeInput value={endsAt} onChange={setEndsAt} />
        </Field>
      </div>
      {match ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Adversário" optional>
            <Input value={opponent} maxLength={120} onChange={(e) => setOpponent(e.target.value)} />
          </Field>
          <Field label="Competição" optional hint="Liga, torneio ou amigável.">
            <Input
              value={competition}
              maxLength={120}
              onChange={(e) => setCompetition(e.target.value)}
            />
          </Field>
        </div>
      ) : null}
      <div className="grid items-end gap-3 sm:grid-cols-2">
        <Field
          label={online ? 'Plataforma ou servidor' : 'Local'}
          optional
          hint={online ? undefined : 'Por exemplo, Arena Lisboa ou a morada.'}
        >
          <Input value={location} maxLength={200} onChange={(e) => setLocation(e.target.value)} />
        </Field>
        <Checkbox label="Online" checked={online} onChange={(e) => setOnline(e.target.checked)} />
      </div>
      <Field label="Notas para a equipa" optional hint="Nunca aparecem na página pública.">
        <Textarea
          value={notes}
          maxLength={2000}
          rows={2}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      {repeat ? (
        <Field
          label="Repetir nas semanas seguintes"
          hint="Quantas semanas mais, à mesma hora. 0 para não repetir."
          error={fields.weeks}
          className="max-w-64"
        >
          <Input
            inputMode="numeric"
            maxLength={2}
            value={weeks}
            onChange={(e) => setWeeks(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {submitLabel}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Voltar
        </Button>
      </div>
    </form>
  );
}
